import { pointsService } from './pointsService';
import { customerService } from './customerService';

export const pointsCsvService = {
  // Gerar template CSV para download
  generateTemplate() {
    const headers = [
      'cpf_cnpj_cliente',
      'pontos',
      'tipo',
      'descricao',
      'funcionario'
    ];

    const sampleData = [
      '"12345678901"',
      '"100"',
      '"purchase"',
      '"Compra no valor de R$ 50,00"',
      '"João Silva"'
    ];

    const instructions = [
      '"INSTRUÇÕES:"',
      '"- Mantenha o cabeçalho (primeira linha) inalterado"',
      '"- CPF/CNPJ do cliente, pontos, tipo e descrição são obrigatórios"',
      '"- CPF apenas números (ex: 12345678901)"',
      '"- CNPJ apenas números (ex: 12345678000195)"',
      '"- Pontos deve ser um número positivo para adicionar"',
      '"- Pontos deve ser um número negativo para resgatar"',
      '"- Tipo: purchase, bonus, referral_bonus, welcome_bonus, manual, redemption"',
      '"- Funcionário: nome de quem está registrando (opcional)"',
      '"- Apague estas linhas de instrução antes de importar"',
      '""',
      '""',
      '"EXEMPLO DE DADOS:"'
    ];

    const csvContent = [
      headers.join(','),
      ...instructions.map(line => line),
      sampleData.join(',')
    ].join('\n');

    return csvContent;
  },

  // Download do template
  downloadTemplate() {
    const csvContent = this.generateTemplate();
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', 'modelo_importacao_pontos.csv');
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  },

  // Parser CSV robusto
  parseCSVLine(line) {
    const result = [];
    let current = '';
    let inQuotes = false;
    
    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      
      if (char === '"') {
        if (inQuotes && line[i + 1] === '"') {
          current += '"';
          i++; // Skip next quote
        } else {
          inQuotes = !inQuotes;
        }
      } else if (char === ',' && !inQuotes) {
        result.push(current.trim());
        current = '';
      } else {
        current += char;
      }
    }
    
    result.push(current.trim());
    return result;
  },

  // Validar formato do CSV
  validateCsvStructure(csvText) {
    const lines = csvText.trim().split('\n');
    
    if (lines.length < 2) {
      throw new Error('O arquivo deve conter pelo menos o cabeçalho e uma linha de dados');
    }

    const headers = this.parseCSVLine(lines[0]).map(h => h.trim().toLowerCase());
    const requiredHeaders = ['pontos', 'tipo', 'descricao'];
    
    // Verificar se tem CPF_CLIENTE ou CPF_CNPJ_CLIENTE
    const hasCpfColumn = headers.includes('cpf_cliente') || headers.includes('cpf_cnpj_cliente');
    if (!hasCpfColumn) {
      requiredHeaders.push('cpf_cnpj_cliente');
    }
    
    const missingHeaders = requiredHeaders.filter(header => 
      !headers.includes(header)
    );

    if (missingHeaders.length > 0) {
      throw new Error(`Colunas obrigatórias ausentes: ${missingHeaders.join(', ')}`);
    }

    // Filtrar linhas de dados válidas (ignorar instruções e linhas vazias)
    const dataLines = lines.slice(1).filter(line => {
      const trimmedLine = line.trim();
      if (!trimmedLine) return false;
      
      // Ignorar linhas que começam com INSTRUÇÕES, EXEMPLO, ou são vazias
      const firstCol = this.parseCSVLine(trimmedLine)[0] || '';
      return !firstCol.toLowerCase().includes('instruções') && 
             !firstCol.toLowerCase().includes('exemplo') &&
             trimmedLine !== '""' &&
             trimmedLine !== '';
    });

    if (dataLines.length === 0) {
      throw new Error('Nenhuma linha de dados válida encontrada no arquivo');
    }

    return { headers, dataLines };
  },

  // Processar linha do CSV
  processLine(line, headers) {
    const values = this.parseCSVLine(line);
    
    const pointsData = {};
    headers.forEach((header, index) => {
      const value = values[index] || '';
      
      switch (header) {
        case 'cpf_cliente':
        case 'cpf_cnpj_cliente':
          pointsData.cpf = value.replace(/[^\d]/g, '');
          break;
        case 'pontos':
          pointsData.points = parseInt(value) || 0;
          break;
        case 'tipo':
          pointsData.type = value.toLowerCase().trim();
          break;
        case 'descricao':
          pointsData.description = value.trim();
          break;
        case 'funcionario':
          pointsData.employeeName = value.trim() || 'Sistema (Importação CSV)';
          break;
      }
    });

    return pointsData;
  },

  // Validar dados de pontos
  validatePointsData(pointsData, lineNumber) {
    const errors = [];

    if (!pointsData.cpf || (pointsData.cpf.length !== 11 && pointsData.cpf.length !== 14)) {
      errors.push(`Linha ${lineNumber}: CPF deve ter 11 dígitos ou CNPJ deve ter 14 dígitos`);
    }

    if (!pointsData.points || isNaN(pointsData.points)) {
      errors.push(`Linha ${lineNumber}: Pontos deve ser um número válido`);
    }

    if (pointsData.points === 0) {
      errors.push(`Linha ${lineNumber}: Pontos não pode ser zero`);
    }

    if (!pointsData.type || pointsData.type.trim() === '') {
      errors.push(`Linha ${lineNumber}: Tipo é obrigatório`);
    }

    const validTypes = ['purchase', 'bonus', 'referral_bonus', 'welcome_bonus', 'manual', 'redemption'];
    if (pointsData.type && !validTypes.includes(pointsData.type)) {
      errors.push(`Linha ${lineNumber}: Tipo inválido. Use: ${validTypes.join(', ')}`);
    }

    if (!pointsData.description || pointsData.description.trim() === '') {
      errors.push(`Linha ${lineNumber}: Descrição é obrigatória`);
    }

    return errors;
  },

  // Processar importação completa
  async processImport(file, companyId) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = async (e) => {
        try {
          const csvText = e.target.result;
          const { headers, dataLines } = this.validateCsvStructure(csvText);
          
          const results = {
            total: dataLines.length,
            processed: 0,
            added: 0,
            redeemed: 0,
            errors: []
          };

          // Processar cada linha
          for (let i = 0; i < dataLines.length; i++) {
            const line = dataLines[i].trim();
            if (!line) continue; // Pular linhas vazias

            const lineNumber = i + 2; // +2 porque começamos do índice 0 e tem o cabeçalho
            
            try {
              const pointsData = this.processLine(line, headers);
              
              // Validar dados
              const validationErrors = this.validatePointsData(pointsData, lineNumber);
              if (validationErrors.length > 0) {
                results.errors.push(...validationErrors);
                continue;
              }

              // Buscar cliente pelo CPF/CNPJ
              const customers = await customerService.getByDocument(pointsData.cpf);
              const customer = customers.find(c => c.companyId === companyId);
              
              if (!customer) {
                results.errors.push(`Linha ${lineNumber}: Cliente com CPF/CNPJ ${pointsData.cpf} não encontrado nesta empresa`);
                continue;
              }

              // Processar pontos
              if (pointsData.points > 0) {
                // Adicionar pontos
                await pointsService.addPoints({
                  customerId: customer.id,
                  companyId: companyId,
                  points: Math.abs(pointsData.points),
                  type: pointsData.type,
                  description: pointsData.description,
                  employeeName: pointsData.employeeName || 'Sistema (Importação CSV)'
                });
                results.added++;
              } else {
                // Resgatar pontos
                await pointsService.redeemPoints({
                  customerId: customer.id,
                  companyId: companyId,
                  points: Math.abs(pointsData.points),
                  type: pointsData.type === 'redemption' ? 'redemption' : 'manual',
                  description: pointsData.description,
                  employeeName: pointsData.employeeName || 'Sistema (Importação CSV)'
                });
                results.redeemed++;
              }
              
              results.processed++;
              
            } catch (error) {
              results.errors.push(`Linha ${lineNumber}: ${error.message}`);
            }
          }

          resolve(results);
          
        } catch (error) {
          reject(error);
        }
      };

      reader.onerror = () => {
        reject(new Error('Erro ao ler o arquivo'));
      };

      reader.readAsText(file, 'UTF-8');
    });
  }
};
